import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { InitialsAvatar } from "../../components/shared/InitialsAvatar";
import { api } from "../../api/client";
import { LogOut, BellRing, Wifi } from "lucide-react";
import { MOBILE } from "../../constants/testIds";
import { toast } from "sonner";

// Convert base64url → Uint8Array for VAPID applicationServerKey
function urlB64ToUint8(b64) {
    const pad = "=".repeat((4 - (b64.length % 4)) % 4);
    const raw = atob((b64 + pad).replace(/-/g, "+").replace(/_/g, "/"));
    return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

async function subscribePush() {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
        toast.error("Push not supported in this browser");
        return;
    }
    const perm = await Notification.requestPermission();
    if (perm !== "granted") {
        toast.error("Notifications blocked");
        return;
    }
    const reg = await navigator.serviceWorker.ready;
    const { data } = await api.get("/v1/push/vapid-public-key");
    if (!data.public_key) {
        toast.error("Push not configured");
        return;
    }
    const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlB64ToUint8(data.public_key),
    });
    const json = sub.toJSON();
    await api.post("/v1/push/subscribe", {
        endpoint: json.endpoint,
        keys: json.keys,
        user_agent: navigator.userAgent,
    });
    toast.success("Push turned on");
}

async function sendTest() {
    try {
        const { data } = await api.post("/v1/push/send-test");
        toast.message(`Sent to ${data.delivered} device(s)`);
    } catch (_) {
        toast.error("Push send failed");
    }
}

export default function Profile() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const doLogout = async () => { await logout(); nav("/login", { replace: true }); };
    return (
        <div data-testid={MOBILE.profileRoot} className="px-5 pb-32">
            <div className="km-card p-5 flex items-center gap-4 mt-2">
                <InitialsAvatar name={user?.name} initials={user?.initials} size={54} />
                <div>
                    <div className="text-lg font-bold">{user?.name}</div>
                    <div className="text-xs text-muted">{user?.email}</div>
                    <div className="km-chip km-chip-navy mt-2">{user?.role}</div>
                </div>
            </div>

            <div className="eyebrow mt-8 mb-3">Notifications</div>
            <button
                data-testid={MOBILE.pushSubscribe}
                onClick={subscribePush}
                className="w-full km-card p-4 flex items-center gap-3"
            >
                <BellRing size={18} className="text-[color:var(--km-navy)]" />
                <div className="flex-1 text-left">
                    <div className="text-sm font-medium">Turn on push</div>
                    <div className="text-xs text-muted">Approvals + schedule changes</div>
                </div>
            </button>
            <button
                data-testid={MOBILE.pushTest}
                onClick={sendTest}
                className="mt-2 w-full km-card p-4 flex items-center gap-3"
            >
                <Wifi size={18} className="text-[color:var(--km-green)]" />
                <div className="flex-1 text-left">
                    <div className="text-sm font-medium">Send test notification</div>
                    <div className="text-xs text-muted">Prove the pipe works</div>
                </div>
            </button>

            <button
                onClick={doLogout}
                data-testid={MOBILE.logoutBtn}
                className="mt-8 w-full h-12 rounded-2xl border border-km bg-white flex items-center justify-center gap-2 text-[color:var(--km-red)] font-medium"
            >
                <LogOut size={16} /> Log out
            </button>
        </div>
    );
}
